import { FileExcelFilled } from '@ant-design/icons';
import { Button, Progress } from 'antd';
import { useEffect, useState } from 'react';
import { apiClient, apiErrorMessage, getApiBaseUrl } from '../../utils';
import { Condition } from '../../models';
import moment from 'moment';

export function ExcelExportPage(props: {
  gridId?: string;
  parentRecordId?: string;
  dataElementFilters?: any;
  dataSetFilters?: any;
  orderBy?: string;
  orderDir?: string;
}) {
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState<string>();

  const baseApiUrl = getApiBaseUrl() + '/api/';

  useEffect(() => {
    setLoading(true);
    const body: any = {
      gridId: props.gridId,
      orderBy: props.orderBy,
      orderDir: props.orderDir,
    };

    const conditions: Condition = {
      logic: 'AND',
      items: [],
    };

    let showAllRevisions = false;

    if (props.parentRecordId) {
      conditions.items.push({
        objectType: 'record',
        objectId: 'MDM_RECORD',
        operator: 'parentRecordIds',
        value: [props.parentRecordId],
      });
    }

    if (props.dataElementFilters) {
      for (let filterKey in props.dataElementFilters) {
        if (!props.dataElementFilters[filterKey]) {
          continue;
        }

        const filter = props.dataElementFilters[filterKey];

        const basicSearchFilterValue = filter[0];
        const isNullFilterValue = filter[1];
        const isNotNullFilterValue = filter[2];
        const isTaskFilterValue = filter[3];
        const isErrorFilterValue = filter[4];
        const advancedFilterValue = filter[5];
        const distinctFilterValue = filter[6];

        if (basicSearchFilterValue !== undefined && basicSearchFilterValue !== null && basicSearchFilterValue.toString().trim() !== '') {
          conditions.items.push({
            objectType: 'dataElement',
            objectId: filterKey,
            operator: 'search',
            value: basicSearchFilterValue,
          });
        }

        if (isNullFilterValue === '1') {
          conditions.items.push({
            objectType: 'dataElement',
            objectId: filterKey,
            operator: 'isNull',
            value: true,
          });
        }

        if (isNotNullFilterValue === '1') {
          conditions.items.push({
            objectType: 'dataElement',
            objectId: filterKey,
            operator: 'isNotNull',
            value: true,
          });
        }

        if (isTaskFilterValue === '1') {
          conditions.items.push({
            objectType: 'dataElement',
            objectId: filterKey,
            operator: 'tasks',
            value: 'true',
          });
        }

        if (isErrorFilterValue === '1') {
          conditions.items.push({
            objectType: 'dataElement',
            objectId: filterKey,
            operator: 'errors',
            value: 'true',
          });
        }

        if (distinctFilterValue) {
          let values = JSON.parse(distinctFilterValue)?.map((v: string | null) => (v === null ? '###null###' : v));
          conditions.items.push({
            objectType: 'dataElement',
            objectId: filterKey,
            operator: 'in',
            value: values,
          });
        }

        if (advancedFilterValue) {
          const advancedFilters = JSON.parse(advancedFilterValue);
          conditions.items.push(advancedFilters);
        }
      }
    }

    if (props.dataSetFilters) {
      if (props.dataSetFilters.activeStatus && props.dataSetFilters.activeStatus !== 'ACTIVE') {
        conditions.items.push({
          objectType: 'record',
          objectId: 'MDM_RECORD',
          operator: 'activeStatus',
          value: props.dataSetFilters.activeStatus === 'INACTIVE' ? 'inactive' : 'all',
        });
        showAllRevisions = true;
      } else {
        conditions.items.push({
          objectType: 'record',
          objectId: 'MDM_RECORD',
          operator: 'activeStatus',
          value: 'active',
        });
      }

      if (props.dataSetFilters.taskStatus && props.dataSetFilters.taskStatus !== 'ALL') {
        conditions.items.push({
          objectType: 'record',
          objectId: 'MDM_RECORD',
          operator: props.dataSetFilters.taskStatus === 'WITH_MY_TASKS' ? 'myTasks' : 'tasks',
          value: props.dataSetFilters.taskStatus === 'WITHOUT_TASKS' ? 'false' : 'true',
        });
        showAllRevisions = true;
      }

      if (props.dataSetFilters.errorStatus && props.dataSetFilters.errorStatus !== 'ALL') {
        conditions.items.push({
          objectType: 'record',
          objectId: 'MDM_RECORD',
          operator: 'errors',
          value: props.dataSetFilters.errorStatus === 'WITH_ERRORS' ? 'true' : 'false',
        });
        showAllRevisions = true;
      }

      if (props.dataSetFilters.lastUpdate && props.dataSetFilters.lastUpdate !== 'ALL') {
        let lastUpdateValue: string = '';
        let subtractDays = 0;
        switch (props.dataSetFilters.lastUpdate) {
          case 'ONE_DAY':
            subtractDays = 1;
            break;
          case 'ONE_WEEK':
            subtractDays = 7;
            break;
          case 'TWO_WEEKS':
            subtractDays = 14;
            break;
          case 'ONE_MONTH':
            subtractDays = 30;
            break;
          case 'ONE_YEAR':
            subtractDays = 365;
            break;
        }
        if (subtractDays > 0) {
          lastUpdateValue = moment.utc().subtract(subtractDays, 'days').startOf('day').format('YYYY-MM-DD');
        }

        conditions.items.push({
          objectType: 'record',
          objectId: 'MDM_RECORD',
          operator: 'lastUpdatedAfter',
          value: lastUpdateValue,
        });
      }

      if (props.dataSetFilters.revisionStatus || showAllRevisions) {
        if (props.dataSetFilters.revisionStatus === 'PREVIOUS' || props.dataSetFilters.revisionStatus === 'ALL' || showAllRevisions) {
          conditions.items.push({
            objectType: 'record',
            objectId: 'MDM_RECORD',
            operator: 'revisions',
            value: props.dataSetFilters.revisionStatus === 'ALL' || showAllRevisions ? 'all' : 'previous',
          });
          showAllRevisions = true;
        } else {
          conditions.items.push({
            objectType: 'record',
            objectId: 'MDM_RECORD',
            operator: 'revisions',
            value: 'LATEST',
          });

          if (props.dataSetFilters.revisionStatus === 'NESTED') {
            body.includeRevisions = true;
          }

          showAllRevisions = false;
        }
      }

      if (props.dataSetFilters.recordStatus && props.dataSetFilters.recordStatus !== 'ALL') {
        conditions.items.push({
          objectType: 'record',
          objectId: 'MDM_RECORD',
          operator: 'processStatus',
          value: props.dataSetFilters.recordStatus === 'PROCESSING' ? 'processing' : 'ready',
        });
      }
    }

    body.conditions = conditions;

    apiClient
      .post(`records/rows/export`, body)
      .then((response: any) => {
        setFileName(response.data?.fileName);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => setLoading(false));
  }, [props.dataElementFilters, props.gridId, props.parentRecordId, props.orderBy, props.orderDir, props.dataSetFilters]);

  return (
    <>
      {loading && (
        <>
          <p>Dosya hazırlanıyor, lütfen bekleyin.</p>
          <Progress status="active" percent={100} showInfo={false} />
        </>
      )}
      {!loading && fileName && (
        <>
          <p>Dosya hazır, aşağıdaki bağlantıdan indirebilirsiniz.</p>
          <a href={`${baseApiUrl}records/rows/export?fileName=${fileName}`}>
            <Button type="primary" icon={<FileExcelFilled />}>
              {fileName}
            </Button>
          </a>
        </>
      )}
    </>
  );
}
